import React from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Eye, Users, FileText, Globe, ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>
            Studio Claris PTY Limited (trading as "Studio Venturis") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, store, and share your information when you use our applications and services.
          </p>
          <p>
            <strong>Company Details:</strong><br />
            Studio Claris PTY Limited<br />
            174B Main Road, Claremont<br />
            Cape Town, South Africa<br />
            Trading as: Studio Venturis
          </p>
          <p>
            We develop applications across multiple industries including healthcare, data management, e-commerce, and financial services. This policy applies to all our applications and services unless otherwise specified.
          </p>
        </div>
      )
    },
    {
      id: "information-collection",
      title: "Information We Collect",
      icon: <Eye className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-white">Personal Information</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Name, email address, phone number</li>
            <li>Billing and payment information</li>
            <li>Profile information and preferences</li>
            <li>Communication history and support interactions</li>
            <li>Health information (for healthcare applications, with explicit consent)</li>
            <li>Financial information (for financial services applications, as required)</li>
          </ul>
          
          <h4 className="text-lg font-semibold text-white">Technical Information</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Device information (model, operating system, unique identifiers)</li>
            <li>IP address and location data</li>
            <li>Usage data and app interactions</li>
            <li>Cookies and similar technologies</li>
            <li>Crash reports and performance data</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Third-Party Information</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Information from social media platforms (when you connect accounts)</li>
            <li>Data from business partners and service providers</li>
            <li>Publicly available information</li>
          </ul>
        </div>
      )
    },
    {
      id: "how-we-use",
      title: "How We Use Your Information",
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>We use your information for the following purposes:</p>
          
          <h4 className="text-lg font-semibold text-white">Service Provision</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Providing and maintaining our applications and services</li>
            <li>Processing transactions and payments</li>
            <li>Personalizing your experience</li>
            <li>Providing customer support</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Communication</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Sending service-related notifications</li>
            <li>Responding to inquiries and support requests</li>
            <li>Marketing communications (with your consent)</li>
            <li>Security alerts and updates</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Legal and Safety</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Complying with legal obligations</li>
            <li>Preventing fraud and security threats</li>
            <li>Enforcing our terms of service</li>
            <li>Protecting rights and safety of users</li>
          </ul>
        </div>
      )
    },
    {
      id: "legal-basis",
      title: "Legal Basis for Processing",
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>Under applicable privacy laws (including GDPR, CCPA, and POPIA), we process your information based on:</p>
          
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li><strong>Consent:</strong> When you provide explicit consent for specific processing activities</li>
            <li><strong>Contract Performance:</strong> To fulfill our contractual obligations to you</li>
            <li><strong>Legitimate Interests:</strong> For our legitimate business interests, balanced against your rights</li>
            <li><strong>Legal Compliance:</strong> To comply with legal obligations</li>
            <li><strong>Vital Interests:</strong> To protect your vital interests or those of others</li>
          </ul>
        </div>
      )
    },
    {
      id: "sharing",
      title: "Information Sharing",
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>We may share your information with:</p>
          
          <h4 className="text-lg font-semibold text-white">Service Providers</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Cloud hosting and storage providers</li>
            <li>Payment processors</li>
            <li>Analytics and marketing platforms</li>
            <li>Customer support tools</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Business Partners</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Integration partners (with your consent)</li>
            <li>Healthcare providers (for health apps, with explicit consent)</li>
            <li>Financial institutions (for financial apps, as required)</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Legal Requirements</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Law enforcement and regulatory authorities</li>
            <li>Legal proceedings and court orders</li>
            <li>Merger, acquisition, or sale of business assets</li>
          </ul>

          <p className="text-sm text-gray-400">
            <strong>Note:</strong> We never sell your personal information for marketing purposes.
          </p>
        </div>
      )
    },
    {
      id: "data-security",
      title: "Data Security",
      icon: <Lock className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>We implement comprehensive security measures to protect your information:</p>
          
          <h4 className="text-lg font-semibold text-white">Technical Safeguards</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>End-to-end encryption for sensitive data</li>
            <li>Secure data transmission (TLS/SSL)</li>
            <li>Regular security audits and penetration testing</li>
            <li>Access controls and authentication</li>
            <li>Data backup and disaster recovery</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Organizational Measures</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Employee training on data protection</li>
            <li>Confidentiality agreements</li>
            <li>Incident response procedures</li>
            <li>Regular compliance reviews</li>
          </ul>

          <p className="text-sm text-yellow-400">
            <strong>Important:</strong> No system is 100% secure. We continuously work to improve our security measures and will notify you of any data breaches as required by law.
          </p>
        </div>
      )
    },
    {
      id: "data-retention",
      title: "Data Retention",
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>We retain your information for as long as necessary to:</p>
          
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Provide our services to you</li>
            <li>Comply with legal obligations</li>
            <li>Resolve disputes and enforce agreements</li>
            <li>Support business operations</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Specific Retention Periods</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li><strong>Account Information:</strong> Until account deletion + 30 days</li>
            <li><strong>Transaction Records:</strong> 7 years (as required by financial regulations)</li>
            <li><strong>Health Data:</strong> As required by healthcare regulations</li>
            <li><strong>Marketing Data:</strong> Until you opt-out or 3 years of inactivity</li>
            <li><strong>Technical Logs:</strong> 12 months maximum</li>
          </ul>
        </div>
      )
    },
    {
      id: "user-rights",
      title: "Your Rights",
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>Under applicable privacy laws, you have the following rights:</p>
          
          <h4 className="text-lg font-semibold text-white">Access and Control</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
            <li><strong>Erasure:</strong> Request deletion of your personal information</li>
            <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Processing Control</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li><strong>Restriction:</strong> Limit how we process your information</li>
            <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Consent Withdrawal:</strong> Withdraw consent at any time</li>
            <li><strong>Automated Decision-Making:</strong> Opt-out of automated profiling</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">How to Exercise Your Rights</h4>
          <p>Contact us at <strong>privacy@studioventuris.com</strong> or through our app settings. We will respond within 30 days (or as required by local law).</p>
          
          <p className="text-sm text-gray-400">
            You also have the right to lodge a complaint with your local data protection authority.
          </p>
        </div>
      )
    },
    {
      id: "international-transfers",
      title: "International Data Transfers",
      icon: <Globe className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>As a South African company, we may transfer your data internationally. We ensure adequate protection through:</p>
          
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Standard Contractual Clauses (SCCs) approved by relevant authorities</li>
            <li>Adequacy decisions by data protection authorities</li>
            <li>Certification schemes and codes of conduct</li>
            <li>Your explicit consent where required</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Primary Data Locations</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>South Africa (primary)</li>
            <li>European Union (GDPR-compliant providers)</li>
            <li>United States (Privacy Shield successors and SCCs)</li>
          </ul>
        </div>
      )
    },
    {
      id: "industry-specific",
      title: "Industry-Specific Provisions",
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-white">Healthcare Applications</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>HIPAA compliance (where applicable)</li>
            <li>Explicit consent for health data processing</li>
            <li>Enhanced security for medical records</li>
            <li>Healthcare provider integration protocols</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Financial Services</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>PCI DSS compliance for payment data</li>
            <li>Financial regulatory reporting</li>
            <li>Anti-money laundering (AML) compliance</li>
            <li>Know Your Customer (KYC) requirements</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">E-commerce Platforms</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Consumer protection compliance</li>
            <li>Merchant and customer data segregation</li>
            <li>Transaction monitoring and fraud prevention</li>
            <li>Digital receipt and warranty management</li>
          </ul>
        </div>
      )
    },
    {
      id: "cookies",
      title: "Cookies and Tracking",
      icon: <Eye className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>We use cookies and similar technologies to enhance your experience:</p>
          
          <h4 className="text-lg font-semibold text-white">Types of Cookies</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li><strong>Essential Cookies:</strong> Required for basic functionality</li>
            <li><strong>Performance Cookies:</strong> Help us understand app usage</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences</li>
            <li><strong>Marketing Cookies:</strong> Personalize advertising (with consent)</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">Your Cookie Choices</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Adjust settings in our cookie banner</li>
            <li>Use browser settings to control cookies</li>
            <li>Opt-out of targeted advertising</li>
            <li>Clear existing cookies at any time</li>
          </ul>
        </div>
      )
    },
    {
      id: "children",
      title: "Children's Privacy",
      icon: <Shield className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>We are committed to protecting children's privacy:</p>
          
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Our services are not directed to children under 13</li>
            <li>We do not knowingly collect information from children under 13</li>
            <li>Parents/guardians can request deletion of child's information</li>
            <li>Enhanced protections for users under 18</li>
            <li>COPPA compliance for US users</li>
          </ul>

          <p className="text-yellow-400">
            <strong>Important:</strong> If you believe we have collected information from a child under 13, please contact us immediately at privacy@studioventuris.com.
          </p>
        </div>
      )
    },
    {
      id: "updates",
      title: "Policy Updates",
      icon: <FileText className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>We may update this privacy policy to reflect:</p>
          
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>Changes in our services or business practices</li>
            <li>New legal requirements</li>
            <li>Improvements in security measures</li>
            <li>User feedback and suggestions</li>
          </ul>

          <h4 className="text-lg font-semibold text-white">How We Notify You</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-300">
            <li>In-app notifications for material changes</li>
            <li>Email notifications to registered users</li>
            <li>Website banner notifications</li>
            <li>Updated "Last Modified" date</li>
          </ul>

          <p className="text-sm text-gray-400">
            Continued use of our services after updates constitutes acceptance of the revised policy.
          </p>
        </div>
      )
    },
    {
      id: "contact",
      title: "Contact Information",
      icon: <Users className="w-6 h-6" />,
      content: (
        <div className="space-y-4">
          <p>For privacy-related questions, concerns, or requests, contact us:</p>
          
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-white mb-4">Privacy Contact Details</h4>
            <div className="space-y-2 text-gray-300">
              <p><strong>Email:</strong> privacy@studioventuris.com</p>
              <p><strong>Data Protection Officer:</strong> dpo@studioventuris.com</p>
              <p><strong>General Inquiries:</strong> hello@studioventuris.com</p>
              <p><strong>Postal Address:</strong><br />
                Studio Claris PTY Limited<br />
                Attention: Privacy Officer<br />
                174B Main Road, Claremont<br />
                Cape Town, 7708<br />
                South Africa
              </p>
            </div>
          </div>

          <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
            <p className="text-sm text-blue-300">
              <strong>Response Time:</strong> We aim to respond to all privacy requests within 30 days (or as required by applicable law). For urgent security matters, contact us immediately.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <motion.header
        className="bg-gray-900/50 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Privacy and Data Protection</h1>
              <p className="text-sm text-gray-400">Studio Claris PTY Limited</p>
            </div>
          </div>
          <div className="text-sm text-gray-400">
            Last Updated: {new Date().toLocaleDateString('en-ZA')}
          </div>
        </div>
      </motion.header>

      {/* Navigation */}
      <nav className="bg-gray-900/30 border-b border-gray-800 sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex flex-wrap gap-4 text-sm">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-gray-400 hover:text-blue-400 transition-colors"
              >
                {section.title}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-16">
          {sections.map((section, index) => (
            <motion.section
              key={section.id}
              id={section.id}
              className="scroll-mt-32"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="text-blue-400">{section.icon}</div>
                <h2 className="text-3xl font-bold">{section.title}</h2>
              </div>
              <div className="prose prose-invert max-w-none">
                {section.content}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Footer */}
        <motion.footer
          className="mt-24 pt-12 border-t border-gray-800 text-center text-gray-400"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="mb-4">
            This privacy policy is effective as of {new Date().toLocaleDateString('en-ZA')} and applies to all Studio Venturis applications and services.
          </p>
          <p className="text-sm">
            © {new Date().getFullYear()} Studio Claris PTY Limited. All rights reserved.
          </p>
        </motion.footer>
      </main>
    </div>
  );
};

export default PrivacyPolicy;